import { combineReducers, compose, createSelector } from '@reduxjs/toolkit';
import { round } from 'lodash';

import { notNull, validCrop } from 'utils/type_guards';
import { calculateBoundDimension } from 'utils/bounds_util';
import { byId } from 'utils/common';

import canvases from 'domains/canvases';
import activeEditor from 'domains/active_editor';
import products from 'domains/products';
import photoModule from 'domains/photos';
import optionGroups from 'domains/option_groups';

import slice from './crops.slice';
import cropsAdapter from './crops.adapter';
import utils from './crops.utils';

import type { RootState } from 'store/wos_store';
import type { PhotoUpload } from 'domains/photos';
import type { CanvasConfiguration } from 'domains/canvases';
import type { CartProductForCreate } from 'domains/cart_products';
import type { PickRequired } from 'types/common_types';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const defaultSelectors = cropsAdapter.getSelectors();

const selectActiveImageNodeUid = (state: LocalState) => state.activeNodeUid;

const selectPhotoByCropId = (state: RootState, cropId: string) => {
  const crop = state.crops.entities[cropId];
  let photo: PhotoUpload | undefined;

  if (crop) {
    photo = state.photos.entities[crop.photoId];
  }

  return photo;
};

const selectCropsByCanvasId = createSelector(
  [compose(defaultSelectors.selectAll, localState), (_: RootState, canvasId: string) => canvasId],
  (crops, canvasId) => crops.filter((crop) => crop.canvasId === canvasId)
);

const selectActiveCrop = (state: RootState) => {
  const activeCanvasId = activeEditor.selectors.canvasId(state);
  const activeNodeUid = selectActiveImageNodeUid(localState(state));

  if (!activeCanvasId || !activeNodeUid) return null;

  const cropId = utils.buildCropId(activeCanvasId, activeNodeUid);

  return defaultSelectors.selectById(localState(state), cropId);
};

const selectActiveNodePhoto = (state: RootState) => {
  const activeCanvasId = activeEditor.selectors.canvasId(state);
  const activeNodeUid = selectActiveImageNodeUid(localState(state));

  if (!activeCanvasId || !activeNodeUid) return null;

  const cropId = utils.buildCropId(activeCanvasId, activeNodeUid);

  return selectPhotoByCropId(state, cropId);
};

const selectCropBlobSrc = (state: LocalState, cropId: string) => {
  const crop = defaultSelectors.selectById(state, cropId);
  return crop?.blobSrc ?? '';
};

const selectPotentialCropIds = createSelector(
  [canvases.selectors.selectAll, products.selectors.selectEntities],
  (allCanvases, productsById) => {
    return allCanvases
      .flatMap((c) => {
        if (!c.product) return null;

        const canvasId = c.id;
        const product = productsById[c.product];

        if (!product) return null;

        const imageNodes = product.nodes.filter((n) => n.node_type === 1);
        return imageNodes.map((n) => utils.buildCropId(canvasId, n.uid));
      })
      .filter(notNull);
  }
);

const selectCropPhotoLoading = (state: LocalState, cropId: string) => {
  const crop = defaultSelectors.selectById(state, cropId);
  return !!crop && !!crop.photoId && !crop.photoLoaded;
};

const selectCropPhotoLoaded = (state: LocalState, cropId: string) => {
  const crop = defaultSelectors.selectById(state, cropId);
  return !!crop && crop.photoLoaded;
};

const selectActiveNodeCropPhotoLoaded = (state: RootState) => {
  const activeCanvasId = activeEditor.selectors.canvasId(state);
  const { activeNodeUid } = state.crops;

  if (!activeCanvasId || !activeNodeUid) return null;

  const cropId = utils.buildCropId(activeCanvasId, activeNodeUid);
  return selectCropPhotoLoaded(localState(state), cropId);
};

const selectCropNodeDimensions = (state: RootState, cropId: string) => {
  const { canvasId, nodeUid } = utils.parseCropId(cropId);

  const canvas = canvases.selectors.selectById(state, canvasId);
  if (!canvas || !canvas.product) return null;

  const product = products.selectors.selectById(state, canvas.product);
  if (!product) return null;

  const productNode = product.nodes.find((node) => node.uid === nodeUid);
  if (!productNode) return null;

  const optionEntries = canvas.options ? Object.entries(canvas.options) : [];
  const overrideNode = optionEntries
    .map(([optionGroupId, optionId]) =>
      optionGroups.selectors.selectById(state, optionGroupId)?.options.find(byId(optionId))
    )
    .filter(notNull)
    .flatMap((option) => option.nodes)
    .filter((node) => node.uid === nodeUid)
    .map(canvases.utils.formatAsOverrideNode)
    .reduce((acc, override) => ({ ...acc, ...override }), {});

  const node = { ...productNode, ...overrideNode };

  const [, , w, h] = node.bounds.split(',');

  return {
    width: calculateBoundDimension(w, 'image', product, canvas),
    height: calculateBoundDimension(h, 'image', product, canvas),
  };
};

const selectCropsForCreateCartProduct = (
  state: RootState,
  canvas: PickRequired<CanvasConfiguration, 'product'>
): CartProductForCreate['cart_photos_attributes'] => {
  const canvasId = canvas.id;
  const crops = selectCropsByCanvasId(state, canvasId);
  const photosById = photoModule.selectors.selectEntities(state);

  return crops.map((crop) => {
    if (!validCrop(crop)) throw new Error('Invalid Crop Data');

    const photo = photosById[crop.photoId];
    if (!photo) throw new Error('No Photo for Provided ID');

    const node = products.selectors.findNode(state, canvas.product, crop.nodeUid);
    if (!node) throw new Error('Product or Node Not Found');

    return {
      node_id: node.id,
      external_asset_id: crop.photoId,
      filename: photo.original_filename,
      url: photo.ink_url,
      rotation: crop.rotation,
      style_filter: utils.cropFilterForCartProduct(crop.filter),
      crop: {
        x: round(crop.cropX * 100, 6),
        y: round(crop.cropY * 100, 6),
        w: round(crop.cropW * 100, 6),
        h: round(crop.cropH * 100, 6),
      },
    };
  });
};

export default {
  ...cropsAdapter.getSelectors(localState),
  activeImageNode: compose(selectActiveImageNodeUid, localState),
  photoLoading: (state: GlobalState, id: string) => selectCropPhotoLoading(localState(state), id),
  photoLoaded: (state: GlobalState, id: string) => selectCropPhotoLoaded(localState(state), id),
  cropBlobSrc: (state: GlobalState, id: string) => selectCropBlobSrc(localState(state), id),

  // Already globalized
  photoByCropId: selectPhotoByCropId,
  cropsForCanvasId: selectCropsByCanvasId,
  activeCrop: selectActiveCrop,
  activeNodePhoto: selectActiveNodePhoto,
  activeNodePhotoLoaded: selectActiveNodeCropPhotoLoaded,
  potentialCropIds: selectPotentialCropIds,
  nodeDimensions: selectCropNodeDimensions,
  forCreateCartProduct: selectCropsForCreateCartProduct,
};
